import { FC, useEffect, useRef, useState } from 'react';

import type { PDFDocumentProxy } from 'pdfjs-dist';

import { markMatches } from './pdfSearch';
import type { PdfJs } from './usePdfDocument';

export type PageHighlight = { term: string; activeOrdinal?: number; scrollRequest?: number };

type PageViewProps = {
  pdfjs: PdfJs;
  pdf: PDFDocumentProxy;
  pageNumber: number;
  pageCount: number;
  scale: number;
  placeholder: { width: number; height: number };
  root: HTMLElement | null;
  suspended: boolean;
  highlight?: PageHighlight;
  register: (pageNumber: number, element: HTMLElement | null) => void;
  onVisibility: (pageNumber: number, ratio: number) => void;
};

const RENDER_MARGIN = '1200px 0px';
const RATIO_STEPS = [0, 0.1, 0.25, 0.5, 0.75, 1];

export const PageView: FC<PageViewProps> = ({
  pdfjs,
  pdf,
  pageNumber,
  pageCount,
  scale,
  placeholder,
  root,
  suspended,
  highlight,
  register,
  onVisibility,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(pageNumber <= 2);
  const [measured, setMeasured] = useState<{ scale: number; width: number; height: number }>();
  const [textReady, setTextReady] = useState(false);
  const handledScrollRequest = useRef<number>();

  useEffect(() => {
    register(pageNumber, containerRef.current);
    return () => register(pageNumber, null);
  }, [pageNumber, register]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => setNear(entry.isIntersecting)),
      { root, rootMargin: RENDER_MARGIN },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [root]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => onVisibility(pageNumber, entry.intersectionRatio)),
      { root, threshold: RATIO_STEPS },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [root, pageNumber, onVisibility]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const textContainer = textRef.current;
    if (!near || suspended || !canvas || !textContainer) {
      return undefined;
    }
    let cancelled = false;
    let renderTask: { cancel: () => void; promise: Promise<unknown> } | undefined;
    let textLayer: { cancel: () => void; render: () => Promise<unknown> } | undefined;

    (async () => {
      const page = await pdf.getPage(pageNumber);
      if (cancelled) {
        return;
      }
      const viewport = page.getViewport({ scale });
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      setMeasured({ scale, width: viewport.width, height: viewport.height });
      const canvasContext = canvas.getContext('2d');
      if (!canvasContext) {
        return;
      }
      renderTask = page.render({
        canvasContext,
        viewport,
        transform: ratio === 1 ? undefined : [ratio, 0, 0, ratio, 0, 0],
        annotationMode: pdfjs.AnnotationMode.ENABLE,
      });
      await renderTask.promise;
      if (cancelled) {
        return;
      }
      textLayer = new pdfjs.TextLayer({
        textContentSource: page.streamTextContent(),
        container: textContainer,
        viewport,
      });
      await textLayer.render();
      if (!cancelled) {
        setTextReady(true);
      }
    })().catch(() => undefined);

    return () => {
      cancelled = true;
      setTextReady(false);
      renderTask?.cancel();
      textLayer?.cancel();
      textContainer.replaceChildren();
      canvas.width = 0;
      canvas.height = 0;
    };
  }, [near, suspended, pdf, pdfjs, pageNumber, scale]);

  const matchTerm = highlight?.term ?? '';
  const matchOrdinal = highlight?.activeOrdinal;
  const scrollRequest = highlight?.scrollRequest;

  useEffect(() => {
    const textContainer = textRef.current;
    if (!textReady || !textContainer) {
      return;
    }
    const active = markMatches(textContainer, matchTerm, matchOrdinal);
    if (!active || scrollRequest === undefined || scrollRequest === handledScrollRequest.current) {
      return;
    }
    handledScrollRequest.current = scrollRequest;
    if (typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'center', inline: 'nearest' });
    }
  }, [textReady, matchTerm, matchOrdinal, scrollRequest]);

  const size = measured && measured.scale === scale ? measured : undefined;
  const width = size?.width ?? placeholder.width;
  const height = size?.height ?? placeholder.height;

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={`Page ${pageNumber} of ${pageCount}`}
      data-page-number={pageNumber}
      className={`relative mx-auto bg-white shadow-lg ${textReady ? '' : 'animate-pulse'}`}
      style={{ width, height, ['--scale-factor' as string]: scale }}
    >
      <canvas ref={canvasRef} aria-hidden="true" className="block h-full w-full" />
      <div ref={textRef} className="irm-text-layer" />
    </div>
  );
};
